import { signOut } from "@bindery/auth-consumer/browser";
import { useLogger } from "@bindery/logging/react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bindery/ui/components/ui/alert-dialog";
import { Button } from "@bindery/ui/components/ui/button";
import { BookOpenIcon } from "@heroicons/react/24/solid";
import { useCallback, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { readerStart } from "@/auth/auto-start";
import { enterReaderSession, type ReaderStart } from "@/auth/enter";
import {
  forgetDeviceReader,
  rememberDeviceReader,
} from "@/storage/device-readers";
import {
  clearUnconfirmed,
  type GateSource,
} from "@/storage/unconfirmed-reader";
import { confirmOfflineReader } from "@/sync/offline-reader";

export const WRONG_BOOKMARK_PATH = "/signed-out?reason=wrong-bookmark";
export const FORGET_TIMEOUT_MS = 5_000;

// Lazy: the route guard renders this gate, and must not open IndexedDB or
// the Convex client just to decide whether to show it.
const forgetLocalReader = (readerId: string): Promise<void> =>
  import("@/sync").then((sync) => sync.forgetLocalReader(readerId));

const FORGET_TIMED_OUT = Symbol("forget timed out");

function withTimeout(
  work: Promise<void>,
  ms: number,
): Promise<void | typeof FORGET_TIMED_OUT> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<typeof FORGET_TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(FORGET_TIMED_OUT), ms);
  });
  return Promise.race([work, timeout]).finally(() => clearTimeout(timer));
}

export interface BookmarkIdentityGateProps {
  /** The reader the bookmark or the device signed in, from the session. */
  readerId: string;
  source?: GateSource;
  /** This device's nickname for that reader, if it has one. */
  nickname: string | null;
  forget?: (readerId: string) => Promise<void>;
  signOutTo?: (returnTo: string) => void;
  startFresh?: (start: ReaderStart) => void;
  onConfirmed?: () => void;
  forgetTimeoutMs?: number;
}

const enter = (start: ReaderStart) => enterReaderSession(start);

const COPY: Record<
  GateSource,
  { title: string; description: string; welcome: string; reject: string }
> = {
  bookmark: {
    title: "Is this your bookmark?",
    description:
      "This bookmark opens a reader's library. Let's make sure it's yours before we start.",
    welcome:
      "This bookmark opens your reading library. Let's make sure it's really you before we start.",
    reject: "Not my bookmark",
  },
  device: {
    title: "Is this you?",
    description:
      "This device remembers a reader. Let's make sure it's you before we start.",
    welcome:
      "This device opens your reading library. Let's make sure it's really you before we start.",
    reject: "Not me",
  },
};

/**
 * A printed bookmark signs in by readerId alone, so a sibling scanning the
 * wrong one would read into another child's study records; a shared device
 * resuming its last reader would do the same. The dialog cannot be
 * dismissed by clicking outside: the child must pick "That's me" or "Not
 * me" before any reading.
 *
 * "Not me" on a scan forgets the scanned reader here. On a device resume
 * nothing was scanned and the reader is still this device's, so it keeps
 * everything and starts the person on a fresh reader instead.
 */
export function BookmarkIdentityGate({
  readerId,
  source = "bookmark",
  nickname,
  forget = forgetLocalReader,
  signOutTo = signOut,
  startFresh = enter,
  onConfirmed,
  forgetTimeoutMs = FORGET_TIMEOUT_MS,
}: BookmarkIdentityGateProps) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const log = useLogger();
  const [phase, setPhase] = useState<"asking" | "rejecting">("asking");
  const [confirmed, setConfirmed] = useState(false);

  const handleConfirm = useCallback(() => {
    log.event("reading.bookmark.identity_confirmed", {
      reader_id: readerId,
      source,
    });
    clearUnconfirmed(readerId);
    rememberDeviceReader(readerId);
    confirmOfflineReader(readerId);
    setConfirmed(true);
    onConfirmed?.();
    void navigate(pathname, { replace: true });
  }, [log, readerId, source, navigate, pathname, onConfirmed]);

  const handleReject = useCallback(async () => {
    setPhase("rejecting");
    log.event("reading.bookmark.identity_rejected", {
      reader_id: readerId,
      source,
    });
    if (source === "device") {
      startFresh(readerStart({ pathname, search }, null));
      return;
    }
    try {
      const outcome = await withTimeout(forget(readerId), forgetTimeoutMs);
      if (outcome === FORGET_TIMED_OUT) {
        log.event("reading.bookmark.forget_timeout", {
          timeout_ms: forgetTimeoutMs,
        });
      }
    } catch (error) {
      log.error("reading.bookmark.forget_failed", error);
    }
    clearUnconfirmed(readerId);
    forgetDeviceReader(readerId);
    signOutTo(WRONG_BOOKMARK_PATH);
  }, [
    log,
    readerId,
    source,
    pathname,
    search,
    forget,
    signOutTo,
    startFresh,
    forgetTimeoutMs,
  ]);

  return (
    <AlertDialog open={!confirmed}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {nickname ? `Welcome back, ${nickname}!` : COPY[source].title}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {nickname ? COPY[source].welcome : COPY[source].description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => void handleReject()}
            disabled={phase === "rejecting"}
          >
            {phase === "rejecting"
              ? "One moment..."
              : nickname
                ? `Not ${nickname}?`
                : COPY[source].reject}
          </Button>
          <Button onClick={handleConfirm} disabled={phase === "rejecting"}>
            That&apos;s me
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function WrongBookmarkScreen() {
  return (
    <div className="bg-background fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 px-6 text-center">
      <BookOpenIcon className="text-ear-teal h-16 w-16" />
      <h1 className="text-foreground text-2xl font-bold">
        This bookmark belongs to another reader
      </h1>
      <p className="text-muted-foreground max-w-md">
        No worries — every reader has their own special bookmark. Ask your
        grown-up to help you find and scan yours, and your own stories will be
        waiting for you.
      </p>
      <Button asChild size="lg">
        <Link to="/">Go to the start page</Link>
      </Button>
    </div>
  );
}
